import React, { Component } from "react";
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import { withRouter } from "react-router-dom";
import SubmenuHeader from "../components/menus/SubMenuHeader";
import { get } from "../js/auth";
import { formatChangeField, formatChangeValue, summarizeChanges, formatModifier, formatModifierOption, formatEntityName } from "../js/admin/menuChangeFormat";

const PAGE_SIZE = 50;
const ENTITY_TYPES = ["item", "category", "Meal", "Drink", "MealCategory", "DrinkCategory", "QuantityType"];
const MODIFICATION_TYPES = ["CREATE", "UPDATE", "DELETE", "RESTORE", "ACTIVATE", "DEACTIVATE", "MOVE", "REORDER"];

const emptyFilter = { from: "", to: "", modifier: "", entityType: "", modificationType: "", text: "" };

const DEFAULT_DAYS = 30;

// datetime-local value (local time, yyyy-MM-ddTHH:mm) of the start of the default period
const defaultFrom = () => {
    const date = new Date(Date.now() - DEFAULT_DAYS * 24 * 60 * 60 * 1000);
    const pad = (n) => (n < 10 ? "0" : "") + n;
    return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate()) + "T" + pad(date.getHours()) + ":" + pad(date.getMinutes());
};

const defaultFilter = () => ({ ...emptyFilter, from: defaultFrom() });

const t = (key) => I18n.t("admin_local.menu_change_log." + key);

const entityTypeLabel = (type) => {
    if (type === "item") return t("type_item");
    if (type === "category") return t("type_category");
    return t(type);
};

class MenuChangeLog extends Component {

    state = {
        filter: defaultFilter(),
        entries: [],
        total: 0,
        offset: 0,
        modifiers: [],
        expanded: {},
        loading: false,
        error: null
    };

    componentDidMount() {
        this.load(0);
    }

    load = (offset) => {
        const filter = this.state.filter;
        const params = ["offset=" + offset, "limit=" + PAGE_SIZE];
        Object.keys(filter).forEach(key => {
            if (filter[key]) params.push(key + "=" + encodeURIComponent(filter[key]));
        });
        this.setState({ loading: true, error: null });
        get("adminService/" + localStorage.restaurantSelected + "/getMenuChangeLog?" + params.join("&"))
            .done(data => {
                this.setState({
                    loading: false,
                    entries: data.entries || [],
                    total: data.total || 0,
                    offset: data.offset || 0,
                    modifiers: data.modifiers || [],
                    expanded: {}
                });
            })
            .fail(() => this.setState({ loading: false, error: t("load_failed") }));
    }

    setFilter = (key, value) => {
        this.setState({ filter: { ...this.state.filter, [key]: value } });
    }

    reset = () => {
        this.setState({ filter: defaultFilter() }, () => this.load(0));
    }

    toggle = (id) => {
        this.setState({ expanded: { ...this.state.expanded, [id]: !this.state.expanded[id] } });
    }

    renderFilter() {
        const { filter, modifiers } = this.state;
        const label = { display: "flex", flexDirection: "column", fontSize: 12, minWidth: 160 };
        const input = { padding: 6, marginTop: 2 };
        return <form
            style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", padding: 12 }}
            onSubmit={e => { e.preventDefault(); this.load(0); }}>
            <label style={label}>{t("time_from")}
                <input type="datetime-local" style={input} value={filter.from} onChange={e => this.setFilter("from", e.target.value)} />
            </label>
            <label style={label}>{t("time_to")}
                <input type="datetime-local" style={input} value={filter.to} onChange={e => this.setFilter("to", e.target.value)} />
            </label>
            <label style={label}>{t("modifier")}
                <select style={input} value={filter.modifier} onChange={e => this.setFilter("modifier", e.target.value)}>
                    <option value="">{t("all_modifiers")}</option>
                    {modifiers.map(m => <option key={m.value} value={m.value}>{formatModifierOption(m)}</option>)}
                </select>
            </label>
            <label style={label}>{t("entity_type")}
                <select style={input} value={filter.entityType} onChange={e => this.setFilter("entityType", e.target.value)}>
                    <option value="">{t("all_entity_types")}</option>
                    {ENTITY_TYPES.map(type => <option key={type} value={type}>{entityTypeLabel(type)}</option>)}
                </select>
            </label>
            <label style={label}>{t("modification_type")}
                <select style={input} value={filter.modificationType} onChange={e => this.setFilter("modificationType", e.target.value)}>
                    <option value="">{t("all_modification_types")}</option>
                    {MODIFICATION_TYPES.map(type => <option key={type} value={type}>{t(type)}</option>)}
                </select>
            </label>
            <label style={label}>{t("text")}
                <input type="text" style={input} value={filter.text} onChange={e => this.setFilter("text", e.target.value)} />
            </label>
            <button type="submit" className="btn btn-primary">{t("search")}</button>
            <button type="button" className="btn btn-default" onClick={this.reset}>{t("reset")}</button>
        </form>;
    }

    renderChanges(entry) {
        const changes = entry.changes || [];
        if (changes.length === 0) {
            return <div style={{ padding: 8 }}>{t("no_details")}</div>;
        }
        return <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
                <tr>
                    <th style={{ textAlign: "left", padding: 4 }}>{t("field")}</th>
                    <th style={{ textAlign: "left", padding: 4 }}>{t("old_value")}</th>
                    <th style={{ textAlign: "left", padding: 4 }}>{t("new_value")}</th>
                </tr>
            </thead>
            <tbody>
                {changes.map((change, index) => <tr key={index} style={{ borderTop: "1px solid #ddd" }}>
                    <td style={{ padding: 4, verticalAlign: "top" }}>{formatChangeField(change.field)}</td>
                    <td style={{ padding: 4, verticalAlign: "top", wordBreak: "break-word" }}>{formatChangeValue(change, "from")}</td>
                    <td style={{ padding: 4, verticalAlign: "top", wordBreak: "break-word" }}>{formatChangeValue(change, "to")}</td>
                </tr>)}
            </tbody>
        </table>;
    }

    renderTable() {
        const { entries, expanded } = this.state;
        if (entries.length === 0) {
            return <div style={{ padding: 12 }}>{this.state.loading ? "" : t("no_results")}</div>;
        }
        const cell = { padding: 6, verticalAlign: "top" };
        return <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
                <tr style={{ textAlign: "left" }}>
                    <th style={cell}></th>
                    <th style={cell}>{t("time")}</th>
                    <th style={cell}>{t("modifier")}</th>
                    <th style={cell}>{t("source")}</th>
                    <th style={cell}>{t("entity")}</th>
                    <th style={cell}>{t("modification_type")}</th>
                    <th style={cell}>{t("changes")}</th>
                </tr>
            </thead>
            <tbody>
                {entries.map(entry => <React.Fragment key={entry.id}>
                    <tr style={{ borderTop: "1px solid #ccc", cursor: "pointer" }} onClick={() => this.toggle(entry.id)}>
                        <td style={cell}>{expanded[entry.id] ? "\u25BE" : "\u25B8"}</td>
                        <td style={cell}>{entry.time ? new Date(entry.time).toLocaleString() : ""}</td>
                        <td style={cell}>{formatModifier(entry)}</td>
                        <td style={cell}>{entry.source ? t("source_" + entry.source) : ""}</td>
                        <td style={cell}>{entryEntity(entry)}</td>
                        <td style={cell}>{t(entry.modificationType)}</td>
                        <td style={{ ...cell, wordBreak: "break-word" }}>{expanded[entry.id] ? "" : summarizeChanges(entry.changes, 3)}</td>
                    </tr>
                    {expanded[entry.id] ?
                        <tr>
                            <td></td>
                            <td colSpan={6} style={{ padding: "0 6px 8px 6px", background: "rgba(0,0,0,0.03)" }}>{this.renderChanges(entry)}</td>
                        </tr> : null}
                </React.Fragment>)}
            </tbody>
        </table>;
    }

    renderPaging() {
        const { offset, total, entries } = this.state;
        if (total === 0) return null;
        return <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 12 }}>
            <button className="btn btn-default" disabled={offset <= 0} onClick={() => this.load(Math.max(0, offset - PAGE_SIZE))}>{t("previous")}</button>
            <span>{(offset + 1) + " - " + (offset + entries.length) + " / " + total}</span>
            <button className="btn btn-default" disabled={offset + PAGE_SIZE >= total} onClick={() => this.load(offset + PAGE_SIZE)}>{t("next")}</button>
        </div>;
    }

    render() {
        if (!this.props.myStatus || !this.props.myStatus.restaurant_id) {
            return <div></div>;
        }
        return <div style={{ flexGrow: 1, flexShrink: 1, display: "flex", flexFlow: "column", overflowY: "hidden", height: "100%" }}>
            <SubmenuHeader {...this.props} header={I18n.t("admin_local.menus.menuChangeLog")} />
            {this.renderFilter()}
            {this.state.error ? <div style={{ padding: 12, color: "#c00" }}>{this.state.error}</div> : null}
            <div style={{ flexGrow: 1, flexShrink: 1, minHeight: 0, overflowY: "auto", padding: "0 12px" }}>
                {this.renderTable()}
            </div>
            <div style={{ flexShrink: 0, borderTop: "1px solid #ddd", background: "#fff" }}>
                {this.renderPaging()}
            </div>
        </div>;
    }
}

function entryEntity(entry) {
    const type = entry.entityType ? t(entry.entityType) : "";
    return type + (entry.entityName ? ": " + formatEntityName(entry.entityName) : "") + (entry.entityId ? " (#" + entry.entityId + ")" : "");
}

const mapStateToProps = (props) => {
    if (props.rootReducer.myStatus && props.rootReducer.myStatus.restaurant_id) {
        return {
            restaurant: props.rootReducer.restaurant,
            myStatus: props.rootReducer.myStatus,
            admin_local: props.rootReducer.admin_local,
            local: props.rootReducer.local
        };
    }
    return {};
};

export default connect(mapStateToProps)(withRouter(MenuChangeLog));
